import { Component, HostListener, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FileValidators } from 'ngx-file-drag-drop';
import { Subscription } from 'rxjs';
import { CampaignsService } from 'src/app/services/campaigns.service';
import Swal from 'sweetalert2';

@Component({ selector: 'app-distribution-art-file',
  templateUrl: './distribution-art-file.component.html', styleUrls: ['./distribution-art-file.component.scss'] })
export class DistributionArtFileComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  fileControl = new FormControl([], [FileValidators.uniqueFileNames, FileValidators.maxFileCount(1)]);
  uploading = false;
  confirming = false;
  delayed = false;
  progress = 0;
  usePresignedUpload = false;
  private subscriptions = new Subscription();
  private confirmationOpen = false;
  @ViewChild('uploadProgress') private uploadProgressTemplate!: TemplateRef<any>;
  private uploadProgressDialog?: MatDialogRef<any>;

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
    private campaignService: CampaignsService, private fb: FormBuilder,
    private matDialog: MatDialog,
    private dialogRef: MatDialogRef<DistributionArtFileComponent>) {}

  ngOnInit(): void {
    this.form = this.fb.group({ blob: [null, Validators.required] });
    this.dialogRef.disableClose = true;
    this.subscriptions.add(this.dialogRef.backdropClick().subscribe(() => this.close()));
    this.subscriptions.add(this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') { this.close(); }
    }));
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (this.uploading) { event.preventDefault(); event.returnValue = ''; }
  }

  close(): void {
    if (this.uploading) {
      Swal.fire({ icon: 'warning', title: 'Carga en curso',
        text: 'Espera a que termine la carga antes de cerrar esta ventana.' });
      return;
    }
    if (!this.confirmationOpen) { this.dialogRef.close(); }
  }

  async saveDistributionArt(): Promise<void> {
    // Solo para artes de distribución: cambiar a false para volver a la subida tradicional.
    const usePresignedUpload = true;
    if (this.uploading || this.confirmationOpen) { return; }
    const file: File | undefined = this.form.value.blob?.[0];
    if (!file) {
      await Swal.fire({ icon: 'warning', title: 'Error', text: 'El documento no ha sido adjuntado' });
      return;
    }
    this.confirmationOpen = true;
    const confirmation = await Swal.fire({ title: 'Con estos cambios el VoBo previo cambiará a "Pendiente"',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Confirmar', cancelButtonText: 'Cancelar' });
    this.confirmationOpen = false;
    if (!confirmation.isConfirmed) { return; }
    this.usePresignedUpload = usePresignedUpload;
    this.uploading = true;
    this.progress = 0;
    this.confirming = false;
    this.fileControl.disable();
    this.openUploadProgress();
    this.subscriptions.add(this.campaignService.uploadDistributionArtFile(file, {
      folder_str: this.data.strategy.folder, folder_camp: this.data.folder_camp,
      id_str: this.data.strategy.id
    }, usePresignedUpload).subscribe({
      next: state => {
        this.progress = state.progress;
        this.confirming = state.confirming;
        this.delayed = !!state.delayed;
        if (state.complete) {
          this.finishUpload();
          Swal.fire({ icon: 'success', title: 'Archivo cargado', text: 'Archivo cargado exitosamente' })
            .then(() => this.dialogRef.close({ reload: true }));
        }
      },
      error: () => {
        this.finishUpload();
        Swal.fire({ icon: 'error', title: 'Error al subir',
          text: 'No se pudo completar la carga. Revisa tu conexión y vuelve a intentarlo.' });
      }
    }));
  }

  private finishUpload(): void {
    this.uploadProgressDialog?.close();
    this.uploadProgressDialog = undefined;
    this.delayed = false;
    this.uploading = false;
    this.confirming = false;
    this.fileControl.enable();
  }

  private openUploadProgress(): void {
    this.uploadProgressDialog = this.matDialog.open(this.uploadProgressTemplate, {
      width: '420px',
      maxWidth: '90vw',
      disableClose: true,
      closeOnNavigation: false,
      ariaLabel: 'Progreso de subida del archivo'
    });
    this.subscriptions.add(this.uploadProgressDialog.backdropClick().subscribe(() => this.close()));
    this.subscriptions.add(this.uploadProgressDialog.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') { this.close(); }
    }));
  }

  onValueChange(incomingFiles: File[]): void {
    if (this.uploading) { return; }
    const validFiles = incomingFiles.filter(file => /\.(zip|xlsx)$/i.test(file.name));
    if (validFiles.length !== incomingFiles.length) {
      Swal.fire({ icon: 'warning', title: 'Error', text: 'Solo se permiten documentos en formato .ZIP o .XLSX.' });
    }
    this.form.controls.blob.setValue(validFiles.slice(0, 1), { emitEvent: false });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.uploadProgressDialog?.close();
  }
}
